import {
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Heading,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { PauseIcon, XCircleIcon } from '@heroicons/react/solid';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionStepStatus } from '@blockpulse3/data/shared';
import { FundraisingInvestStatus, useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';
import { useSubscriptionBadge } from '@blockpulse3/ui/ui-hooks';

import * as SubscriptionStepsComponents from './Steps';
import { isStepDisabled } from './utils';

type Props = BoxProps;

/**
 * SubscriptionSteps.
 *
 * @returns {JSX.Element}
 */
export function SubscriptionSteps({ ...props }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');

  const { subscriptionId = '' } = useParams();

  /* ** Fetch subscription ** */
  const { data, loading, error } = useGetSubscriptionQuery({ variables: { subscriptionId } });

  const { subscription = null } = data || {};
  const subscriptionBadge = useSubscriptionBadge(subscription);

  if (loading) {
    return (
      <Skeleton {...props}>
        <Card h="375px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard {...props} h="375px" />;
  }

  if (!subscription) {
    return <ErrorQueryCard {...props} h="375px" />;
  }

  /* ** Compute completed steps ** */
  const subscriptionSteps = subscription.data.mergedSteps;
  const completeSteps =
    subscriptionSteps?.reduce(
      (acc, step) => (step.status === SubscriptionStepStatus.VALID ? acc + 1 : acc),
      0,
    ) || 0;

  // List available components for steps
  const StepsComponents: Record<
    string,
    ({
      subscriptionStep,
      isDisabled,
    }: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscriptionStep: any;
      isDisabled: boolean;
    }) => JSX.Element
  > = {};
  const { MifidModal, ...restSubscriptionStepsComponents } = SubscriptionStepsComponents;
  Object.entries(restSubscriptionStepsComponents).forEach(([name, value]) => {
    return (StepsComponents[name] = value);
  });

  return (
    <Card {...props}>
      <CardHeader>
        <Stack alignItems="flex-start" spacing="0">
          <HStack alignItems="center">
            <Heading size="md">{i18nSubscription('Title')}</Heading>
            <Badge colorScheme={subscriptionBadge.color}>{subscriptionBadge.label}</Badge>
          </HStack>
          {subscription?.investStatus !== FundraisingInvestStatus.REFUSED && (
            <Text fontSize="sm">
              {i18nSubscription.rich('Subtitle', {
                completeSteps,
                totalStep: subscriptionSteps.length,
                important: (chunks) => (
                  <Text as="span" fontWeight="bold">
                    {chunks}
                  </Text>
                ),
              })}
            </Text>
          )}
        </Stack>
      </CardHeader>
      <CardBody>
        {subscription?.investStatus === FundraisingInvestStatus.REFUSED ||
        !subscriptionSteps?.length ? (
          <Alert status="error">
            <AlertIcon as={XCircleIcon} />
            <AlertTitle>{i18nSubscription('ExpiredSubscription')}</AlertTitle>
          </Alert>
        ) : (
          <Stack spacing="3">
            {subscription.locked && (
              <Alert status="warning">
                <AlertIcon as={PauseIcon} />
                <AlertTitle>{i18nSubscription('LockedSubscription')}</AlertTitle>
              </Alert>
            )}
            {subscriptionSteps.map((step, index) => {
              if (step.__typename) {
                const isDisabled = subscription.locked || isStepDisabled(subscriptionSteps, index);
                const StepComponent = StepsComponents[step.__typename.replace('Merged', '')];
                return (
                  <StepComponent
                    key={step.subscriptionStepId}
                    isDisabled={isDisabled}
                    subscriptionStep={step}
                  />
                );
              }
              return null;
            })}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
}

export type SubscriptionStepsProps = Props;
